import React, { useRef, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import { useState } from 'react'
import CountUp from 'react-countup'

function RetirementSolverBanner({
  bannerID = 'retirementSolver',
  clickAction = () => console.log('clicked retirement solver'),
  parcoAnnuityRecommendationBasedOnLifestyle = {},
  neededForRetirementNumber = 0,
  neededForRetirementFull = 0,
  neededForRetirementTrimmed = 0,
  neededForRetirementParco = 0,
}) {
  const [isHovered, setIsHovered] = useState(false)
  // const MotionBox = motion(Box)
  const MotionBox = motion(Box, { forwardMotionProps: true })
  const container = {
    animate: {
      transition: {
        staggerChildren: 1,
      },
    },
    hovered: {
      // This empty variant will stop the stagger when hovered
    },
  }

  const dollarFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
  const lastRef = useRef(0)
  useEffect(() => {
    lastRef.current = neededForRetirementNumber
  }, [neededForRetirementNumber])

  const arrow = {
    initial: { x: '-30%', opacity: 0.2 },
    animate: {
      x: '20%',
      opacity: 0,
      transition: {
        duration: 10,
        repeat: Infinity,
        repeatDelay: 0.1,
      },
    },
    // The state to transition to when we want to hide the arrows.
    hovered: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  }

  const circle = {
    initial: { scale: 1, opacity: 0.4 },
    animate: {
      scale: 2,
      opacity: 0,
      transition: {
        duration: 2,
        repeatDelay: 0.1,
        repeat: Infinity,
        ease: 'easeOut',
      },
    },
  }

  // handle click to prevent double firing
  const lastClickRef = useRef(0)

  const handleClick = (e) => {
    const now = Date.now()

    // Prevent double firing (onTap + onClick firing together)
    if (now - lastClickRef.current < 300) {
      return
    }

    lastClickRef.current = now
    clickAction()
  }

  return (
    <MotionBox
      sx={{
        boxShadow: `0px 4px 25px -8px rgba(0, 0, 0, 0.15),
                0px -2px 12px -6px rgba(0, 0, 0, 0.08)`,
        cursor: 'pointer',
        borderRadius: { xs: '12px', md: '24px' },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        height: 125,
        backgroundColor: '#2370ed',
        zIndex: 5,
        pointerEvents: 'auto',
        tabIndex: 0,
        role: 'button',
      }}
      whileTap={{ scale: 0.98 }}
      whileHover="hover"
      initial="initial"
      onHoverStart={() => {
        setIsHovered(true)
      }}
      onHoverEnd={() => {
        setIsHovered(false)
      }}
      // onClick={handleClick}
      onTap={handleClick}
      onMouseDown={handleClick}
      id="retirementSolverBanner"
    >
      {/* RADIATING ARROW */}
      <motion.div
        variants={container}
        initial="initial"
        animate={isHovered ? 'hovered' : 'animate'}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {[...Array(7)].map((_, i) => (
          <motion.div
            key={i}
            variants={arrow}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.1,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Box sx={{ flex: 1, backgroundColor: '#fff' }} />
            <Box
              sx={{
                position: 'absolute',
                width: 125,
                height: 125,
                backgroundColor: '#fff',
                borderRadius: '16px',
                transform: 'rotate(45deg)',
                right: -40,
                pointerEvents: 'none',
              }}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* White sliding overlay */}
      <motion.div
        variants={{
          initial: { x: '-25%' },
          hover: { x: '-55%' },
        }}
        transition={{ type: 'spring', stiffness: 250, damping: 25 }}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'row',
          pointerEvents: 'none', // ensure cursor works
          zIndex: 0,
        }}
      >
        <Box sx={{ flex: 1, backgroundColor: '#fff', pointerEvents: 'none' }} />
        <Box
          sx={{
            position: 'relative',
            width: 125,
            height: 125,
            backgroundColor: '#fff',
            borderRadius: 3,
            transform: 'rotate(45deg)',
            right: -40,
            top: 0,
            pointerEvents: 'none',
          }}
        />
      </motion.div>

      {/* Left content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 21,
          px: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'left',
          color: '#262627',
        }}
      >
        <Typography sx={{ opacity: '90%' }} variant="body2Fun">
          NEEDED FOR RETIREMENT
        </Typography>
        <Typography mt={1} variant="headerTitle">
          <CountUp
            start={lastRef.current}
            end={neededForRetirementNumber}
            duration={0.5}
            formattingFn={(val) => dollarFormat.format(val)}
          />
        </Typography>
      </Box>

      {/* Right content (animated reveal) */}
      <motion.div
        variants={{
          initial: { opacity: 0, x: 30 },
          hover: { opacity: 1, x: 0 },
        }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        style={{
          position: 'relative',
          zIndex: 3,
          textAlign: 'right',

          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          paddingRight: 72,
          color: '#fff',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            alignSelf: 'flex-end',
            width: 28,
            height: 28,
            backgroundColor: '#fff',
            borderRadius: 300,
            // transform: 'rotate(45deg)',
            right: 20,
            top: -12,
            pointerEvents: 'none',
          }}
        >
          <motion.div
            variants={container}
            initial="initial"
            animate={isHovered ? 'animate' : 'initial'}
            style={{
              position: 'relative',
              // right: 24,
              // top: -8,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                variants={circle}
                style={{
                  position: 'absolute',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  transformOrigin: 'center',
                  pointerEvents: 'none',
                }}
              />
            ))}
          </motion.div>
        </Box>
        {/* radiating circle */}

        <Typography sx={{ opacity: '90%' }} variant="body2Fun">
          DO IT FOR LESS
        </Typography>
        <Typography mt={1} variant="headerTitle">
          {neededForRetirementParco}
        </Typography>
      </motion.div>
    </MotionBox>
  )
}

export default RetirementSolverBanner
